/**
 * A utility object for handling browser cookies.
 * 
 * This utility provides methods to set, get, and delete cookies in the browser.
 * It simplifies cookie management with straightforward methods and includes
 * optional settings, such as expiration dates for cookies.
 * 
 * @file Utility object for managing browser cookies.
 */

const Cookie = {
    /**
     * Sets a cookie in the browser with a specified name, value, and expiration.
     * 
     * This method creates a cookie with a specified name and value, and sets
     * its expiration to the given number of days from the current date. By default,
     * the cookie expires in 30 days if no value is provided for the `days` parameter.
     * 
     * @param {string} name - The name of the cookie.
     * @param {string} value - The value of the cookie.
     * @param {number} [days=7] - The number of days until the cookie expires. Defaults to 30 days.
     */
    set(name, value, days = 30) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/`;
    },

    /**
     * Retrieves the value of a cookie by name.
     * 
     * This method searches for a cookie by name and returns its value. If the cookie
     * is not found, the method returns `null`.
     * 
     * @param {string} name - The name of the cookie to retrieve.
     * @returns {string|null} - The value of the cookie, or null if the cookie does not exist.
     */
    get(name) {
        return document.cookie
            .split('; ')
            .find(row => row.startsWith(`${name}=`))
            ?.split('=')[1] || null;
    },

    /**
     * Deletes a cookie by setting its expiration date to a past date.
     * 
     * This method effectively removes a cookie by setting its expiration date
     * to January 1, 1970, making it expire immediately.
     * 
     * @param {string} name - The name of the cookie to delete.
     */
    delete(name) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
};

export default Cookie;